import type { Object3D } from 'three/webgpu';
import {
  AssemblyComponentFixationService,
  AssemblyComponentFixationState,
} from '../component-fixation';
import {
  createAssemblyTransformHistoryRecord,
  createComponentInstanceSnapshotFromObject,
} from '../component-instance';
import type { AssemblyTransformHistoryRecord } from '../component-instance';

/**
 * 固定/浮动历史应用输入。
 */
export interface ApplyAssemblyFixationStateWithHistoryInput {
  /** 目标组件对象。 */
  readonly object: Object3D | null | undefined;
  /** 目标固定/浮动状态。 */
  readonly fixationState: AssemblyComponentFixationState;
  /** 所属装配体 ID。 */
  readonly assemblyId?: string;
  /** 组件实例 ID。 */
  readonly componentInstanceId?: string;
}

/**
 * 带历史记录能力地写入固定/浮动状态。
 *
 * 设计说明：
 * 当前 Fix / Float 仍由导入层 hierarchy 与 Object3D metadata 共同承载。这里先把 Object3D
 * 主状态变更收口为 before/after record，后续 web-client 可在刷新 feature tree 的同时提交事务。
 *
 * @param input 固定/浮动写入输入。
 * @returns 发生语义变化时返回历史记录，否则返回 null。
 */
export function applyAssemblyFixationStateWithHistory(
  input: ApplyAssemblyFixationStateWithHistoryInput,
): AssemblyTransformHistoryRecord | null {
  if (!input.object) {
    return null;
  }

  const before = createComponentInstanceSnapshotFromObject({
    object: input.object,
    assemblyId: input.assemblyId,
    componentInstanceId: input.componentInstanceId,
  });

  AssemblyComponentFixationService.writeSceneObjectFixationState(input.object, input.fixationState);

  const after = createComponentInstanceSnapshotFromObject({
    object: input.object,
    assemblyId: before.assemblyId,
    componentInstanceId: before.componentInstanceId,
  });

  return createAssemblyTransformHistoryRecord(
    input.fixationState === AssemblyComponentFixationState.Fixed ? 'fix' : 'float',
    before,
    after,
  );
}
